import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { StyleService } from '../../services/style.service';
import {
    ICreateStyleRequest, IStyle, IStyleAttachment, IStyleCostLineRequest, StyleStatus,
} from '../../interfaces/garments/style.interface';
import { BuyerService } from '../../services/buyer.service';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { PERMISSIONS } from '../../constants/permissions';
import BuyerAutocomplete from './BuyerAutocomplete';
import StyleCostSheet from './StyleCostSheet';
import StyleAttachments from './StyleAttachments';
import StyleBomEditor from './StyleBomEditor';
import { enumLabel } from '../../utils/garmentsLabels';

// Mirrors `CreateStyleRequest`'s @Size bounds exactly (backend/.../merch/dataclass/CreateStyleRequest.java).
const MAX = {
    styleNumber: 60, name: 255, buyerStyleRef: 120, garmentType: 120, season: 60, sizeRange: 120,
    fabricDescription: 255, constructionNotes: 1000, currencyCode: 3, hsCode: 20,
};

const STATUS_COLORS: Record<StyleStatus, 'default' | 'info' | 'primary' | 'success' | 'warning'> = {
    DEVELOPMENT: 'info',
    QUOTED: 'primary',
    CONFIRMED: 'success',
    ARCHIVED: 'default',
};

interface FormState {
    buyer: IBuyer | null;
    styleNumber: string;
    name: string;
    buyerStyleRef: string;
    garmentType: string;
    season: string;
    sizeRange: string;
    fabricDescription: string;
    constructionNotes: string;
    targetFobPrice: string;
    currencyCode: string;
    hsCode: string;
}

const emptyForm: FormState = {
    buyer: null, styleNumber: '', name: '', buyerStyleRef: '', garmentType: '', season: '',
    sizeRange: '', fabricDescription: '', constructionNotes: '', targetFobPrice: '', currencyCode: '',
    hsCode: '',
};

/** Create/edit screen for a style (garments-merchandising T8) — hosts the cost sheet
 *  (`StyleCostSheet`) and attachment list (`StyleAttachments`) alongside the style's own fields.
 *  New/edit route: `/merchandising/styles/new` and `/merchandising/styles/:id`. */
export function StyleForm() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const toast = useToast();
    const canManage = hasPermission(PERMISSIONS.STYLE_MANAGE);
    const isEdit = Boolean(id) && id !== 'new';

    const [style, setStyle] = useState<IStyle | null>(null);
    const [attachments, setAttachments] = useState<IStyleAttachment[]>([]);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [costSheetSaving, setCostSheetSaving] = useState(false);

    const loadAttachments = useCallback(async (styleId: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            const res = await StyleService.listAttachments(institutionId, styleId);
            setAttachments(res);
        } catch (e) {
            toast.showApiError(e, 'Failed to load attachments');
        }
    }, [toast]);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !id || !isEdit) return;
        try {
            setLoading(true);
            const res = await StyleService.getById(institutionId, id);
            setStyle(res);
            const buyer = await BuyerService.getById(institutionId, res.buyerId);
            setForm(prev => ({ ...prev, buyer }));
            await loadAttachments(res.id);
        } catch (e) {
            toast.showApiError(e, 'Failed to load style');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    // `toast`/`loadAttachments` are deliberately excluded from `load`'s own deps above and from
    // this effect's deps below — `toast` is a fresh object on every render (from context, and even
    // more so from a test double), and depending on it here would refetch the style on every
    // unrelated re-render instead of only when the route's `id` changes.
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    useEffect(() => {
        if (!style) return;
        setForm(prev => ({
            ...prev,
            styleNumber: style.styleNumber,
            name: style.name,
            buyerStyleRef: style.buyerStyleRef ?? '',
            garmentType: style.garmentType ?? '',
            season: style.season ?? '',
            sizeRange: style.sizeRange ?? '',
            fabricDescription: style.fabricDescription ?? '',
            constructionNotes: style.constructionNotes ?? '',
            targetFobPrice: style.targetFobPrice != null ? String(style.targetFobPrice) : '',
            currencyCode: style.currencyCode ?? '',
            hsCode: style.hsCode ?? '',
        }));
    }, [style]);

    const setField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [field]: e.target.value }));

    const validate = (): Record<string, string> => {
        const next: Record<string, string> = {};
        if (!form.buyer) next.buyer = 'Buyer is required';
        if (!form.styleNumber.trim()) next.styleNumber = 'Style number is required';
        if (!form.name.trim()) next.name = 'Name is required';
        if (form.targetFobPrice.trim() && (Number.isNaN(Number(form.targetFobPrice)) || Number(form.targetFobPrice) < 0)) {
            next.targetFobPrice = 'Enter a non-negative price';
        }
        return next;
    };

    const handleSave = async () => {
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !form.buyer) return;

        const request: ICreateStyleRequest = {
            buyerId: form.buyer.id,
            styleNumber: form.styleNumber.trim(),
            name: form.name.trim(),
            buyerStyleRef: form.buyerStyleRef.trim() || null,
            garmentType: form.garmentType.trim() || null,
            season: form.season.trim() || null,
            sizeRange: form.sizeRange.trim() || null,
            fabricDescription: form.fabricDescription.trim() || null,
            constructionNotes: form.constructionNotes.trim() || null,
            targetFobPrice: form.targetFobPrice.trim() === '' ? null : Number(form.targetFobPrice),
            currencyCode: form.currencyCode.trim() || null,
            hsCode: form.hsCode.trim() || null,
        };

        setSaving(true);
        try {
            if (isEdit && style) {
                const updated = await StyleService.update(institutionId, style.id, request);
                setStyle(updated);
                toast.showSuccess('Style updated');
            } else {
                const created = await StyleService.create(institutionId, request);
                toast.showSuccess('Style created');
                navigate(`/merchandising/styles/${created.id}`, { replace: true });
            }
        } catch (e) {
            toast.showApiError(e, isEdit ? 'Failed to update style' : 'Failed to create style');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !style) return;
        const verb = style.active ? 'archive' : 'reactivate';
        if (!window.confirm(`Are you sure you want to ${verb} "${style.name}"?`)) return;
        try {
            const updated = style.active
                ? await StyleService.archive(institutionId, style.id)
                : await StyleService.activate(institutionId, style.id);
            setStyle(updated);
            toast.showSuccess(style.active ? 'Style archived' : 'Style reactivated');
        } catch (e) {
            toast.showApiError(e, `Failed to ${verb} style`);
        }
    };

    const handleChangeStage = async (target: 'DEVELOPMENT' | 'QUOTED' | 'CONFIRMED') => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !style || target === style.status) return;
        try {
            const updated = await StyleService.changeStage(institutionId, style.id, target);
            setStyle(updated);
            toast.showSuccess(`Style moved to ${enumLabel(target)}`);
        } catch (e) {
            toast.showApiError(e, 'Failed to change style stage');
        }
    };

    const handleSaveCostLines = async (requests: IStyleCostLineRequest[]) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !style) return;
        setCostSheetSaving(true);
        try {
            const updated = await StyleService.replaceCostLines(institutionId, style.id, requests);
            setStyle(updated);
            toast.showSuccess('Cost sheet saved');
        } catch (e) {
            toast.showApiError(e, 'Failed to save cost sheet');
        } finally {
            setCostSheetSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.styleForm.page">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button data-help="merchandising.styleForm.back" startIcon={<ArrowBackIcon />} onClick={() => navigate('/merchandising/styles')}>
                    Back
                </Button>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {isEdit ? `Edit Style${style ? `: ${style.styleNumber}` : ''}` : 'New Style'}
                </Typography>
                {style && (
                    <>
                        <Chip
                            data-help="merchandising.styleForm.stage"
                            label={enumLabel(style.status)}
                            color={STATUS_COLORS[style.status]}
                            size="small"
                        />
                        <Chip
                            label={style.active ? 'Active' : 'Inactive'}
                            color={style.active ? 'success' : 'default'}
                            variant={style.active ? 'filled' : 'outlined'}
                            size="small"
                        />
                        {canManage && style.active && style.status !== 'ARCHIVED' && (
                            <TextField
                                select size="small" label="Stage" value={style.status}
                                onChange={e => handleChangeStage(e.target.value as 'DEVELOPMENT' | 'QUOTED' | 'CONFIRMED')}
                                sx={{ minWidth: 150 }} data-testid="style-stage-select" data-help="merchandising.styleForm.changeStage"
                            >
                                <MenuItem value="DEVELOPMENT">Development</MenuItem>
                                <MenuItem value="QUOTED">Quoted</MenuItem>
                                <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                            </TextField>
                        )}
                        {canManage && (
                            <Button
                                size="small"
                                color={style.active ? 'warning' : 'success'}
                                data-help="merchandising.styleForm.archive" onClick={handleToggleActive}
                            >
                                {style.active ? 'Archive' : 'Reactivate'}
                            </Button>
                        )}
                    </>
                )}
            </Box>

            <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-help-section="merchandising.styleForm.details">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <BuyerAutocomplete
                        data-help="merchandising.styleForm.field.buyer"
                        value={form.buyer}
                        onChange={buyer => setForm(prev => ({ ...prev, buyer }))}
                        activeOnly
                        disabled={!canManage}
                        required
                        error={!!errors.buyer}
                        helperText={errors.buyer}
                    />
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.styleForm.field.styleNumber"
                            label="Style Number" value={form.styleNumber} onChange={setField('styleNumber')}
                            error={!!errors.styleNumber} helperText={errors.styleNumber}
                            inputProps={{ maxLength: MAX.styleNumber }} disabled={!canManage} required fullWidth
                        />
                        <TextField
                            data-help="merchandising.styleForm.field.name"
                            label="Name" value={form.name} onChange={setField('name')}
                            error={!!errors.name} helperText={errors.name}
                            inputProps={{ maxLength: MAX.name }} disabled={!canManage} required fullWidth
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.styleForm.field.buyerStyleRef"
                            label="Buyer Style Ref" value={form.buyerStyleRef} onChange={setField('buyerStyleRef')}
                            inputProps={{ maxLength: MAX.buyerStyleRef }} disabled={!canManage} fullWidth
                        />
                        <TextField
                            data-help="merchandising.styleForm.field.garmentType"
                            label="Garment Type" value={form.garmentType} onChange={setField('garmentType')}
                            inputProps={{ maxLength: MAX.garmentType }} disabled={!canManage} fullWidth
                        />
                        <TextField
                            data-help="merchandising.styleForm.field.hsCode"
                            label="HS Code" value={form.hsCode} onChange={setField('hsCode')}
                            inputProps={{ maxLength: MAX.hsCode }} disabled={!canManage} fullWidth
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.styleForm.field.season"
                            label="Season" value={form.season} onChange={setField('season')}
                            inputProps={{ maxLength: MAX.season }} disabled={!canManage} fullWidth
                        />
                        <TextField
                            data-help="merchandising.styleForm.field.sizeRange"
                            label="Size Range" value={form.sizeRange} onChange={setField('sizeRange')}
                            inputProps={{ maxLength: MAX.sizeRange }} disabled={!canManage} fullWidth
                        />
                    </Box>
                    <TextField
                        data-help="merchandising.styleForm.field.fabricDescription"
                        label="Fabric Description" value={form.fabricDescription} onChange={setField('fabricDescription')}
                        inputProps={{ maxLength: MAX.fabricDescription }} disabled={!canManage} fullWidth
                    />
                    <TextField
                        data-help="merchandising.styleForm.field.constructionNotes"
                        label="Construction Notes" value={form.constructionNotes} onChange={setField('constructionNotes')}
                        inputProps={{ maxLength: MAX.constructionNotes }} multiline minRows={2} disabled={!canManage} fullWidth
                    />
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.styleForm.field.targetFobPrice"
                            label="Target FOB Price" type="number" value={form.targetFobPrice}
                            onChange={setField('targetFobPrice')}
                            error={!!errors.targetFobPrice} helperText={errors.targetFobPrice}
                            disabled={!canManage} fullWidth
                        />
                        <TextField
                            select data-help="merchandising.styleForm.field.currencyCode" label="Currency Code" value={form.currencyCode || ''}
                            onChange={setField('currencyCode')}
                            disabled={!canManage} fullWidth
                        >
                            <MenuItem value="">(institution default)</MenuItem>
                            {['USD', 'EUR', 'GBP', 'BDT'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                    </Box>
                    {style && (
                        <Typography variant="body2" color="text.secondary">
                            Total cost: {style.totalCost.toFixed(2)}
                            {style.estimatedMarginPct != null && style.totalCost > 0 && ` · Estimated margin: ${style.estimatedMarginPct.toFixed(2)}%`}
                        </Typography>
                    )}
                    {canManage && (
                        <Box>
                            <Button data-help="merchandising.styleForm.save" variant="contained" onClick={handleSave} disabled={saving}>
                                {saving ? <CircularProgress size={20} /> : 'Save'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Paper>

            {style && (
                <>
                    <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-help-section="merchandising.styleForm.costSheet">
                        <StyleCostSheet
                            costLines={style.costLines}
                            totalCost={style.totalCost}
                            canManage={canManage}
                            saving={costSheetSaving}
                            onSave={handleSaveCostLines}
                        />
                    </Paper>
                    <Divider sx={{ mb: 3 }} />
                    <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-help-section="merchandising.styleForm.bom">
                        <StyleBomEditor styleId={style.id} canManage={canManage} />
                    </Paper>
                    <Divider sx={{ mb: 3 }} />
                    <Paper variant="outlined" sx={{ p: 3 }}>
                        <StyleAttachments
                            styleId={style.id}
                            attachments={attachments}
                            canManage={canManage}
                            onChanged={() => loadAttachments(style.id)}
                        />
                    </Paper>
                </>
            )}
        </Box>
    );
}

export default StyleForm;
